import React from 'react'

import { Alert, AppSvg, Button, FormattedCurrency, FormattedHTMLMessage, newIcons, Status } from '@web/walter-ui-kit'
import cn from 'classnames'
import { getAmountClassName, getTxSvgName } from './utils'
import s from './s.module.css'
import { lang } from './lang'
import TxRows from './TxRows'
import { useRows, useChallenge } from './hooks'
import { Transaction } from '~pages/CardsPage/Cards/Transactions/types'
import { usePromise } from '~hooks/usePromise'
import { cardsApi, useGetThreeDSChallengesData } from '~api'
import useToast from '~hooks/useToast'

interface Props {
    data?: Transaction
    onAppModalOpen?: () => void
    onClose?: () => void
}

const showSymbol = (type: Transaction['transaction_type']) => {
    if (type === 'deposit') {
        return '+'
    }

    if (type === 'payment') {
        return '-'
    }

    return null
}

// eslint-disable-next-line complexity
const getStatus = (status?: Transaction['status'], isCountdownActive?: boolean, isChallenge?: boolean) => {
    if (isChallenge && !isCountdownActive) {
        return 'error'
    }

    if (status === 'Authorized') {
        return 'pending'
    }

    if (status === 'Declined' || status === 'Reversed') {
        return 'error'
    }

    if (status === 'Cleared' || status === 'Settled' || status === 'Refunded' || status === 'Chargeback') {
        return 'success'
    }

    return undefined
}

// eslint-disable-next-line max-lines-per-function,complexity
function Content({ data, onAppModalOpen, onClose }: Props) {
    const svgName = getTxSvgName(data?.transaction_type)
    const amountClassName = getAmountClassName(data?.transaction_type, data?.status)
    const { mutate: mutateChallenges } = useGetThreeDSChallengesData()

    const { timeString, isCountdownActive, isChallenge } = useChallenge(data)

    const rows = useRows(data, isChallenge)

    const status = getStatus(data?.status, isCountdownActive, isChallenge)
    const pushApprove = useToast({
        caption: lang.approveToast,
        icon: <AppSvg svgName={newIcons.ic20CheckCircle} size={20} path="new-icons" className="text-text-positive" />,
    })
    const pushReject = useToast({
        caption: lang.rejectToast,
        icon: <AppSvg svgName={newIcons.ic16ErrorCircle} size={20} path="new-icons" className="text-text-negative" />,
    })

    const { send: onApprove, isLoading: isApproveLoading } = usePromise(async () => {
        if (data?.method !== 'OUTOFBAND') {
            onClose?.()
            return onAppModalOpen?.()
        }

        await cardsApi.postThreeDSResolveOutofband({
            id: data?.id || '',
            resolution: 'APPROVED',
        })

        await mutateChallenges()
        pushApprove()
        onClose?.()
    })

    const { send: onReject, isLoading: isRejectLoading } = usePromise(async () => {
        await cardsApi.postThreeDSResolveOutofband({
            id: data?.id || '',
            resolution: 'REJECTED',
        })

        await mutateChallenges()
        pushReject()
        onClose?.()
    })

    return (
        <div className={s.wrap}>
            <div className={s.icon}>{svgName && <AppSvg size={24} svgName={svgName} path="new-icons" />}</div>
            <div className={s.middle}>
                <div className={cn(s.amount, amountClassName)}>
                    {showSymbol(data?.transaction_type)}
                    <FormattedCurrency
                        maxPrecision={2}
                        minimumFractionDigits={2}
                        value={data?.amount}
                        currency={data?.currency || ''}
                        dataPointer="amount"
                    />
                </div>
                {status && (
                    <div className={s.status}>
                        <Status
                            icon={
                                // eslint-disable-next-line no-nested-ternary
                                isChallenge ? (
                                    isCountdownActive ? (
                                        <AppSvg svgName={newIcons.ic24TimeCircle} size={20} path="new-icons" />
                                    ) : (
                                        <AppSvg svgName={newIcons.ic16ErrorCircle} size={20} path="new-icons" />
                                    )
                                ) : undefined
                            }
                            status={status}
                            dataPointer={`status_${status}`}
                        >
                            {/* eslint-disable-next-line no-nested-ternary */}
                            {isChallenge ? (
                                isCountdownActive ? (
                                    <FormattedHTMLMessage {...lang.expires} values={{ timeString }} />
                                ) : (
                                    <FormattedHTMLMessage {...lang.expired} />
                                )
                            ) : (
                                <FormattedHTMLMessage {...lang[status]} />
                            )}
                        </Status>
                    </div>
                )}
            </div>
            <div className={s.bottom}>
                <TxRows rows={rows} />
            </div>
            {isChallenge && (
                <div className={s.approvePart}>
                    {isCountdownActive ? (
                        <>
                            <Alert
                                icon={<AppSvg svgName={newIcons.ic16Alert} size={16} path="new-icons" />}
                                dataPointer="alert"
                                text={<FormattedHTMLMessage {...lang.alert} />}
                                type="warning"
                                className={s.alert}
                            />
                            <div className={s.button}>
                                <Button
                                    prefix={<AppSvg svgName={newIcons.ic24Close} size={24} path="new-icons" />}
                                    dataPointer="decline"
                                    size="l"
                                    fullWidth
                                    htmlButtonType="button"
                                    isDisabled={isApproveLoading}
                                    isLoading={isRejectLoading}
                                    onClick={onReject}
                                >
                                    <FormattedHTMLMessage {...lang.decline} />
                                </Button>
                                <Button
                                    prefix={<AppSvg svgName={newIcons.ic24Check} size={24} path="new-icons" />}
                                    dataPointer="approve"
                                    size="l"
                                    mode="primary"
                                    htmlButtonType="submit"
                                    isDisabled={isRejectLoading}
                                    isLoading={isApproveLoading}
                                    onClick={onApprove}
                                    fullWidth
                                >
                                    <FormattedHTMLMessage {...lang.approve} />
                                </Button>
                            </div>
                        </>
                    ) : (
                        <Button dataPointer="close" size="l" fullWidth htmlButtonType="button" onClick={onClose}>
                            <FormattedHTMLMessage {...lang.close} />
                        </Button>
                    )}
                </div>
            )}
        </div>
    )
}

export default Content
